import * as m from "~/paraglide/messages.js";

/**
 * The four phases of an engagement, and the artifact each one leaves behind.
 *
 * Each phase carries a different sheet rather than the same card four times:
 * a brief, a test sheet, a handover checklist, an improvement log. That is the
 * argument the page makes — the work is handed over in pieces you can hold.
 */

export type PhaseId = "build" | "discover" | "improve" | "launch";

/** The scope agreed at the end of a phase. */
interface Deliverable {
  label: string;
  value: string;
}

interface BriefSheet {
  kind: "brief";
  marker: string;
  rows: { term: string; value: string }[];
  title: string;
}

interface CheckSheet {
  cases: { label: string; needsPerson?: boolean; state: string }[];
  kind: "check";
  note: string;
  title: string;
}

interface HandoverSheet {
  items: string[];
  kind: "handover";
  title: string;
}

interface LogSheet {
  entries: { body: string; title: string }[];
  kind: "log";
  note: string;
  title: string;
}

export type PhaseSheet = BriefSheet | CheckSheet | HandoverSheet | LogSheet;

export interface ProcessPhase {
  /** Two paragraphs; the design sets them as separate blocks. */
  body: [string, string];
  deliverable: Deliverable;
  id: PhaseId;
  /** "01 / Understand" — stage number and name, above the nav title. */
  navStage: string;
  navTitle: string;
  /** Rendered aria-hidden beside the text; navStage already announces it. */
  number: string;
  sheet: PhaseSheet;
  /** Two lines; the design breaks the headline deliberately. */
  title: [string, string];
}

export function getProcessPhases(): ProcessPhase[] {
  return [
    {
      body: [m.how_we_work_discover_body_1(), m.how_we_work_discover_body_2()],
      deliverable: {
        label: m.how_we_work_discover_deliverable_label(),
        value: m.how_we_work_discover_deliverable_value(),
      },
      id: "discover",
      navStage: m.how_we_work_discover_stage(),
      navTitle: m.how_we_work_discover_nav(),
      number: "01",
      sheet: {
        kind: "brief",
        marker: m.how_we_work_brief_marker(),
        rows: [
          {
            term: m.how_we_work_brief_task_term(),
            value: m.how_we_work_brief_task_value(),
          },
          {
            term: m.how_we_work_brief_sources_term(),
            value: m.how_we_work_brief_sources_value(),
          },
          {
            term: m.how_we_work_brief_boundary_term(),
            value: m.how_we_work_brief_boundary_value(),
          },
          {
            term: m.how_we_work_brief_result_term(),
            value: m.how_we_work_brief_result_value(),
          },
          {
            term: m.how_we_work_brief_exceptions_term(),
            value: m.how_we_work_brief_exceptions_value(),
          },
        ],
        title: m.how_we_work_brief_title(),
      },
      title: [
        m.how_we_work_discover_title_1(),
        m.how_we_work_discover_title_2(),
      ],
    },
    {
      body: [m.how_we_work_build_body_1(), m.how_we_work_build_body_2()],
      deliverable: {
        label: m.how_we_work_build_deliverable_label(),
        value: m.how_we_work_build_deliverable_value(),
      },
      id: "build",
      navStage: m.how_we_work_build_stage(),
      navTitle: m.how_we_work_build_nav(),
      number: "02",
      sheet: {
        // Two of the four escalate rather than resolve. That ratio is the
        // point: the page is claiming judgment stays with a person.
        cases: [
          {
            label: m.how_we_work_check_case_1(),
            state: m.how_we_work_check_state_1(),
          },
          {
            label: m.how_we_work_check_case_2(),
            needsPerson: true,
            state: m.how_we_work_check_state_2(),
          },
          {
            label: m.how_we_work_check_case_3(),
            state: m.how_we_work_check_state_3(),
          },
          {
            label: m.how_we_work_check_case_4(),
            needsPerson: true,
            state: m.how_we_work_check_state_4(),
          },
        ],
        kind: "check",
        note: m.how_we_work_check_note(),
        title: m.how_we_work_check_title(),
      },
      title: [m.how_we_work_build_title_1(), m.how_we_work_build_title_2()],
    },
    {
      body: [m.how_we_work_launch_body_1(), m.how_we_work_launch_body_2()],
      deliverable: {
        label: m.how_we_work_launch_deliverable_label(),
        value: m.how_we_work_launch_deliverable_value(),
      },
      id: "launch",
      navStage: m.how_we_work_launch_stage(),
      navTitle: m.how_we_work_launch_nav(),
      number: "03",
      sheet: {
        items: [
          m.how_we_work_handover_item_1(),
          m.how_we_work_handover_item_2(),
          m.how_we_work_handover_item_3(),
          m.how_we_work_handover_item_4(),
        ],
        kind: "handover",
        title: m.how_we_work_handover_title(),
      },
      title: [m.how_we_work_launch_title_1(), m.how_we_work_launch_title_2()],
    },
    {
      body: [m.how_we_work_improve_body_1(), m.how_we_work_improve_body_2()],
      deliverable: {
        label: m.how_we_work_improve_deliverable_label(),
        value: m.how_we_work_improve_deliverable_value(),
      },
      id: "improve",
      navStage: m.how_we_work_improve_stage(),
      navTitle: m.how_we_work_improve_nav(),
      number: "04",
      sheet: {
        entries: [
          {
            body: m.how_we_work_log_entry_1_body(),
            title: m.how_we_work_log_entry_1_title(),
          },
          {
            body: m.how_we_work_log_entry_2_body(),
            title: m.how_we_work_log_entry_2_title(),
          },
          {
            body: m.how_we_work_log_entry_3_body(),
            title: m.how_we_work_log_entry_3_title(),
          },
        ],
        kind: "log",
        note: m.how_we_work_log_note(),
        title: m.how_we_work_log_title(),
      },
      title: [m.how_we_work_improve_title_1(), m.how_we_work_improve_title_2()],
    },
  ];
}

/** What to bring to a first conversation. Three things, not a brief. */
export function getPreparePoints(): string[] {
  return [
    m.how_we_work_prepare_item_1(),
    m.how_we_work_prepare_item_2(),
    m.how_we_work_prepare_item_3(),
  ];
}
